import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Paper } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useDispatch, useSelector } from '../../../../redux/store';
import CalendarPage from './CalendarPage';
import { getVendorDatesData, postVendorDateForm, postDeleteVendorDate } from '../../../../api/vendors';
import { onCloseModal } from '../../../../redux/slices/vendors';

VendorShipDateTab.propTypes = {
  selectedRow: PropTypes.object,
};

const getSelectedFacilityId = (facility) => facility?.id === 'all-facilities' ? 0 : facility?.id;

const getDaysArray = (start, end) => {
  const arr = [];
  const dt = new Date(start);
  end = new Date(end);

  while (dt <= end) {
    arr.push(dt.getDay());
    dt.setDate(dt.getDate() + 1);
  }

  return [...new Set(arr)];
}

export default function VendorShipDateTab({ selectedRow }) {
  const dispatch = useDispatch();

  const { data: { facilities }, selectedEventId } = useSelector((state) => state.vendors);
  const [facility, setFacility] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('dayGridMonth');
 
  function updateWeeklyEvents(events) {
    return events.flatMap(event => [
      ...(event.cutoffStartDate && event.cutoffEndDate ? [{ ...event, startDate: event.cutoffStartDate, endDate: event.cutoffEndDate, title: 'Cutoff' }] : []),
      ...(event.shipDate ? [{ ...event, startDate: event.shipDate, title: 'Ship Day', allDay: true }] : []),
    ]).map(event => {
      if (facility?.id === 'all-facilities') event.title = `${event.title} - ${facilities.find(f => f.value === event.facilityId)?.label || ''}`;
      event.color = event.weekly ? '#1890FF' : event.biweekly ? '#8BC34A' : '#FFC107';
      const startDate = new Date(event.startDate);
      if (event.biweekly) event.rrule = { freq: 'weekly', interval: 2, dtstart: startDate };
      if (event.allDay) return event.weekly ? { ...event, daysOfWeek: [startDate.getDay()] } : event.biweekly ? event : { ...event, start: startDate };
      if (event.weekly || event.biweekly) {
        event.daysOfWeek = getDaysArray(startDate, new Date(event.endDate));
        event.startTime = event.startDate.split('T')[1].substring(0, 5);
        event.endTime = event.endDate.split('T')[1].substring(0, 5);
        return event;
      }
      return { ...event, end: new Date(event.endDate), start: startDate };
    });
  }

  useEffect(() => {
    if (selectedRow) setVendor({ id: selectedRow.id, label: selectedRow.name });
  }, [selectedRow]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getVendorDatesData(getSelectedFacilityId(facility), vendor?.id);
      const updatedEvents = updateWeeklyEvents(response.data);
      setEvents(updatedEvents);
    } catch (error) {
      enqueueSnackbar("Error fetching event data", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {  
    if (vendor && facility) fetchData();
  }, [facility, vendor]);

  const handleDelete = async () => {
    try {
      const response = await postDeleteVendorDate(selectedEventId);
      if (response.status === 200) await fetchData();
      dispatch(onCloseModal());
    } catch (error) {
      enqueueSnackbar("Failed to delete event", { variant: "error" });
    }
  };

  const handleEditAddDate = async (data) => {
    try {
      const response = await postVendorDateForm(data, selectedEventId);
      if (response.status === 200 && response.data) {
        enqueueSnackbar('Date saved successfully', { variant: 'success' });
        await fetchData();
      } else enqueueSnackbar('Invalid schedule. Enter a ship date; cutoff times must both be entered or left blank, with start before end.', { variant: 'error' });
      dispatch(onCloseModal());
    } catch (error) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
    }
  };

  return <Paper sx={{ px: 2, mt: 1, minHeight: '50vh', boxShadow: ' 0px 2px 4px 1px rgba(0, 0, 0, 0.2)' }}>
    <CalendarPage
      events={events}
      handleDelete={handleDelete}
      handleEditAddDate={handleEditAddDate}
      view={view}
      facility={facility}
      vendor={vendor}
      setVendor={setVendor}
      setFacility={setFacility}
      setView={setView}
      loading={loading}
    />
  </Paper>;
}
